import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { retailerLocationDataApiUrlCreator } from '@lib/core/retailers/slices/urls';
import { IRetailerLocationsRequestParams } from '@lib/core/retailers/types';
import { IRetailerLocation } from '@lib/core/retailers/types/retailerLocation';
import { isRetailerLocationDefault, isRetailerLocationKiosk } from '@lib/core/retailers/utils/filters';
import { isApplicationKiosk, isApplicationPmi } from '@lib/core/service/consts';
import { parseError } from '@lib/core/service/requests/parseError';
import { request } from '@lib/core/service/requests/request';

export interface IRetailerLocationState {
  isLoading: boolean;
  data: IRetailerLocation;
  list: IRetailerLocation[];
}

const initialState: IRetailerLocationState = {
  data: undefined,
  isLoading: false,
  list: [],
};

export const actionGetRetailerLocations = createAsyncThunk(
  'retailerLocations/get',
  async (params: IRetailerLocationsRequestParams) => {
    const response = await request({ url: retailerLocationDataApiUrlCreator(params) });
    const retailerLocation = response.length
      ? response.find(isApplicationKiosk ? isRetailerLocationKiosk : isRetailerLocationDefault)
      : response;

    const {
      product_categories: productCategoriesRaw,
      design_sets: designSetsRaw,
      addons: addonsRaw,
    } = retailerLocation;

    const productCategories = [];
    productCategoriesRaw.forEach(productCategory => productCategories.push(productCategory.name));
    const addons = [];
    addonsRaw.forEach(addon => addons.push(addon.name));

    const preparedData = retailerLocation;
    if (preparedData.design_sets?.length) {
      const assignedDesignSets = [];
      const allowedDesignSets = {};
      let currentAssignedDesignSets = [];

      designSetsRaw.forEach(designSet => {
        const designSetProductCategories = designSet.product_categories;
        designSetProductCategories.forEach(product => {
          assignedDesignSets.push({ [product.name]: designSet.name });
        });
      });

      let productDesignSets = [];

      if (isApplicationPmi) {
        productCategories.forEach(product => {
          productDesignSets = assignedDesignSets.filter(ds => Object.keys(ds)[0] === product);

          currentAssignedDesignSets.push(...productDesignSets);
        });
      } else {
        currentAssignedDesignSets = assignedDesignSets;
      }

      currentAssignedDesignSets.forEach(designSet => {
        productCategories.forEach(product => {
          if (designSet[product]) allowedDesignSets[product] = designSet[product];
        });
      });

      preparedData.design_sets = allowedDesignSets;
      preparedData.product_categories = productCategories;
      preparedData.addons = addons;
    } else {
      return Promise.reject(parseError('No design_sets found for this retailer location'));
    }

    return {
      retailerLocation,
      retailerLocationsList: response.length ? response : null,
    };
  },
);

export const actionSetRetailerLocationDetail = createAsyncThunk(
  'retailerLocation/set',
  (retailerLocationData: IRetailerLocation) => {
    return retailerLocationData;
  },
);

export const retailerLocationSlice = createSlice({
  extraReducers: builder => {
    builder.addCase(actionGetRetailerLocations.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(
      actionGetRetailerLocations.fulfilled,
      (
        state,
        action: PayloadAction<{ retailerLocation: IRetailerLocation; retailerLocationsList: IRetailerLocation[] }>,
      ) => {
        const {
          payload: { retailerLocation, retailerLocationsList },
        } = action;
        state.isLoading = false;
        state.data = state.data || retailerLocation;
        state.list = retailerLocationsList || state.list;
      },
    );
    builder.addCase(actionSetRetailerLocationDetail.fulfilled, (state, action: PayloadAction<IRetailerLocation>) => {
      const { payload } = action;
      state.isLoading = false;
      state.data = payload;
    });
    builder.addCase(actionGetRetailerLocations.rejected, state => {
      state.isLoading = false;
    });
  },
  initialState,
  name: 'retailerLocation',
  reducers: {
    updateRetailerLocationAddons: (state, { payload }) => {
      if (state.data) state.data.addons = payload;
    },
  },
});

export const { updateRetailerLocationAddons } = retailerLocationSlice.actions;
export default retailerLocationSlice.reducer;
